import { useEffect, useState } from 'react';
import { useApp } from '../app/store.ts';
import { archive } from '../archive/store.ts';
import { UNSICHER } from '../archive/ocr.ts';
import { api } from '../data/api.ts';
import { ensureAttachment, listAttachments } from '../data/attachments.ts';
import { onDocsChanged } from '../data/docs.ts';
import type { ArchiveEntry } from '../archive/store.ts';
import type { AttachmentMeta } from '../data/attachments.ts';

/**
 * Der Streifen über einem Dokument (docs/KONZEPT.md, Abschnitt 19).
 *
 * Das Dokument selbst ist eine Notiz und wird im gewohnten Editor bearbeitet — dort
 * stehen Titel, eigene Notiz und Schlagwörter. Was ein Dokument darüber hinaus hat,
 * steht hier: die unveränderliche Datei, die beiden Daten und der gewonnene Text.
 */

const QUELLE: Record<string, string> = {
  text: 'Text übernommen',
  pdf: 'Text aus dem PDF übernommen',
  docx: 'Text aus dem Word-Dokument übernommen',
  eml: 'Text aus der E-Mail übernommen',
  ocr: 'auf dem Gerät erkannt',
  claude: 'über Claude erkannt',
  keine: 'noch kein Text — Erkennung steht aus',
};

/** `YYYY-MM-DD` für das Datumsfeld; leer, wenn kein Datum bekannt ist. */
function asInput(at: number | null): string {
  if (at === null) return '';
  const date = new Date(at);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function DocumentStrip({ note }: { note: ArchiveEntry }): JSX.Element {
  const { refresh, open } = useApp();
  const [file, setFile] = useState<{ id: string; meta: AttachmentMeta } | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [showText, setShowText] = useState(false);
  const [running, setRunning] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [remoteOk, setRemoteOk] = useState(false);
  const meta = note.dokument;
  const unsicher = meta.konfidenz != null && meta.konfidenz < UNSICHER;

  async function erkennen(ueberClaude: boolean): Promise<void> {
    setRunning(ueberClaude ? 'Wird an Claude geschickt …' : 'Text wird erkannt …');
    const options = {
      hatDatum: meta.datum !== null,
      onProgress: (progress: { page: number; pages: number }) =>
        setRunning(
          progress.pages > 1
            ? `Seite ${progress.page} von ${progress.pages}`
            : ueberClaude
              ? 'Wird an Claude geschickt …'
              : 'Text wird erkannt …',
        ),
    };
    setProblem(null);
    try {
      const ergebnis = ueberClaude
        ? await archive.recognizeRemote(note.id, options)
        : await archive.recognize(note.id, options);
      if (!ergebnis) setProblem('Aus dieser Datei war kein Text zu holen.');
      await refresh();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Erkennung fehlgeschlagen.');
    } finally {
      setRunning(null);
      void archive.release();
    }
  }

  /** Deckblatt neu zeichnen — für die Kacheln, bei denen es beim Aufnehmen misslang. */
  async function deckblatt(): Promise<void> {
    setRunning('Deckblatt wird erzeugt …');
    setProblem(null);
    try {
      const gelungen = await archive.rethumb(note.id);
      if (!gelungen) {
        setProblem('Aus dieser Datei lässt sich kein Bild gewinnen — die Kachel zeigt weiter den Text.');
      }
      await refresh();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Das Deckblatt ließ sich nicht erzeugen.');
    } finally {
      setRunning(null);
    }
  }

  useEffect(() => {
    void api
      .ocrStatus()
      .then((status) => setRemoteOk(status.available))
      .catch(() => setRemoteOk(false));
  }, []);

  useEffect(() => {
    let alive = true;
    const nachsehen = (): void => {
      void listAttachments(note.id).then((all) => {
        if (!alive) return;
        const first = Object.entries(all)[0];
        setGesucht(true);
        if (first) setFile({ id: first[0], meta: first[1] });
      });
    };
    nachsehen();

    // Beim Aufnehmen erscheint das Dokument, bevor sein Upload durch ist. Vorher sah
    // der Streifen genau einmal nach und behauptete danach für immer, es hänge keine
    // Datei daran. An `updatedAt` zu hängen genügt nicht: Beim endgültigen Schreiben
    // ändert sich der Zeitstempel nicht, wohl aber das Dokument.
    const stop = onDocsChanged((id) => {
      if (id === note.id) nachsehen();
    });
    return () => {
      alive = false;
      stop();
    };
  }, [note.id]);

  useEffect(() => {
    if (!file) return;
    // Die Datei kommt erst beim Öffnen — offline liegen nur Text und Daten.
    void ensureAttachment(note.id, file.id).then(setUrl);
  }, [note.id, file]);

  const image = file?.meta.mime.startsWith('image/') === true;
  // `file === null` heißt nicht „lädt noch": Nach dem ersten Durchlauf steht fest, ob
  // eine Datei am Dokument hängt. Fehlt sie, ist das die wichtigste Nachricht auf
  // dieser Seite — ein Dokument ohne Datei ist eine leere Hülle.
  const [gesucht, setGesucht] = useState(false);

  return (
    <div className="doc-strip">
      <div className="doc-strip-row">
        <span className="doc-badge">Eingang {new Date(meta.eingang).toLocaleDateString('de-DE')}</span>
        <label className="doc-date">
          Dokumentdatum
          <input
            type="date"
            value={asInput(meta.datum)}
            onChange={(event) => {
              const value = event.target.value;
              void archive
                .setDate(note.id, value ? new Date(`${value}T12:00:00`).getTime() : null)
                .then(refresh);
            }}
          />
        </label>
        <button
          className="ghost"
          onClick={() =>
            void archive
              .setStatus(note.id, meta.status === 'archiv' ? 'posteingang' : 'archiv')
              .then(refresh)
          }
        >
          {meta.status === 'archiv' ? 'Zurück in den Posteingang' : 'Ins Archiv legen'}
        </button>
      </div>

      {file && (
        <div className="doc-file-row">
          {url ? (
            <a href={url} target="_blank" rel="noreferrer">
              {file.meta.name}
            </a>
          ) : (
            <span className="muted">{file.meta.name} — wird geladen …</span>
          )}
          <span className="muted">{Math.round(file.meta.size / 1024)} KiB</span>
        </div>
      )}

      {image && url && <img className="doc-preview" src={url} alt={file?.meta.name ?? ''} />}

      {gesucht && !file && meta.unvollstaendig && (
        <p className="hint">Die Datei wird gerade hochgeladen …</p>
      )}

      {gesucht && !file && !meta.unvollstaendig && (
        <>
          <p className="warn">
            An diesem Dokument hängt keine Datei — beim Aufnehmen ist der Upload
            gescheitert.
          </p>
          <div className="doc-strip-row">
            <button
              className="ghost"
              onClick={() => {
                // Endgültig, nicht in den Papierkorb: Ohne Datei gibt es nichts zu
                // bewahren, und die Prüfsumme muss weg, damit dieselbe Datei erneut
                // aufgenommen werden kann.
                void archive.purge(note.id).then(() => {
                  void refresh();
                  open(null);
                });
              }}
            >
              Löschen und neu aufnehmen
            </button>
          </div>
        </>
      )}

      <p className={unsicher ? 'warn' : 'hint'}>
        {QUELLE[meta.quelle] ?? meta.quelle}
        {meta.konfidenz != null && ` — Konfidenz ${meta.konfidenz}%`}
        {unsicher &&
          '. Die Erkennung ist sich nicht sicher; Zahlen und Namen bitte gegenlesen.'}
      </p>

      <div className="doc-strip-row">
        {running ? (
          <span className="muted">{running}</span>
        ) : (
          <>
            <button className="doc-file" onClick={() => void erkennen(false)}>
              {meta.quelle === 'keine' ? 'Text erkennen' : 'Erneut auf dem Gerät erkennen'}
            </button>
            {file && (
              <button className="doc-file" onClick={() => void deckblatt()}>
                {meta.vorschau ? 'Deckblatt erneuern' : 'Deckblatt erzeugen'}
              </button>
            )}
            {remoteOk && (
              <button
                className={unsicher || meta.quelle === 'keine' ? 'ghost' : 'doc-file'}
                onClick={() => void erkennen(true)}
              >
                Besser erkennen
              </button>
            )}
          </>
        )}
      </div>
      {problem && <p className="warn">{problem}</p>}

      {remoteOk && !running && (
        <p className="hint">
          „Besser erkennen“ schickt die Seiten als Bild an die Claude-Schnittstelle —
          der einzige Weg, auf dem ein Dokument dieses Gerät verlässt. Von selbst
          passiert das nie.
        </p>
      )}

      {meta.erkannt !== '' && (
        <>
          <button className="doc-file" onClick={() => setShowText(!showText)}>
            {showText ? 'Text ausblenden' : `Gewonnenen Text zeigen (${meta.erkannt.length} Zeichen)`}
          </button>
          {showText && <pre className="doc-text">{meta.erkannt}</pre>}
        </>
      )}
    </div>
  );
}
